// src/modules/vehiculos/components/VehiculoViewerDialog.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Stack,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import PublicIcon from "@mui/icons-material/Public";

import { buscarVehiculoGlobal } from "../modules/vehiculos/services/vehiculosService";
import MiniMapaVehiculo from "../modules/vehiculos/components/MiniMapaVehiculo";
import JsonKeyValueTable from "../modules/vehiculos/components/JsonKeyValueTable";

// mismos mapas que tu BuscarVehiculoPage
const coloresFuente = {
  "YPFB internacional": "primary",
  "YPFB nacional": "secondary",
  rastreo: "success",
  rastreo_bo: "info",
  flespi: "warning",
  wialon: "error",
};

const iconosFuente = {
  "YPFB internacional": <LocalShippingIcon fontSize="small" />,
  "YPFB nacional": <LocalShippingIcon fontSize="small" />,
  rastreo: <GpsFixedIcon fontSize="small" />,
  rastreo_bo: <GpsFixedIcon fontSize="small" />,
  flespi: <SatelliteAltIcon fontSize="small" />,
  wialon: <PublicIcon fontSize="small" />,
};

const VehiculoViewerDialog = ({ open, onClose, placa }) => {
  const [resultado, setResultado] = useState(null); // { data: [...] }
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const vehiculos = resultado?.data ?? [];
  const seleccionado =
    selectedIndex != null && vehiculos.length > 0
      ? vehiculos[selectedIndex]
      : null;

  // estilo scroll igual a tu página
  const scrollContainerStyle = useMemo(
    () => ({
      overflowY: "auto",
      flexGrow: 1,
      "&::-webkit-scrollbar": { width: "8px" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.light,
        borderRadius: "4px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: theme.palette.grey[100],
      },
      p: 2,
      ...(isMobile && { p: 1 }),
    }),
    [theme, isMobile]
  );

  const ExtrasView = ({ seleccionado }) => {
    return <JsonKeyValueTable data={seleccionado?.extras || {}} />;
  };

  // Cargar datos cada vez que se abra el modal con una placa
  useEffect(() => {
    if (!open || !placa) return;

    const fetchData = async () => {
      setCargando(true);
      setError(null);
      setResultado(null);
      setSelectedIndex(null);

      try {
        const data = await buscarVehiculoGlobal(placa);
        setResultado(data);
        const tieneResultados = (data?.data?.length ?? 0) > 0;
        setSelectedIndex(tieneResultados ? 0 : null);
      } catch (err) {
        console.error(err);
        setError("Error al buscar vehículo por placa.");
      } finally {
        setCargando(false);
      }
    };

    fetchData();
  }, [open, placa]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Buscar vehículo por placa{" "}
        <Typography component="span" sx={{ fontWeight: "bold" }}>
          {placa || "—"}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Estado de carga / error */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {cargando && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Buscando vehículo...
            </Typography>
          </Box>
        )}

        {!cargando && !error && (
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              height: isMobile ? "auto" : 520,
            }}
          >
            {/* Columna izquierda: tabla (como en BuscarVehiculoPage) */}
            <Box
              sx={{
                width: isMobile ? "100%" : "55%",
                borderRight: isMobile ? "none" : 1,
                borderColor: "grey.200",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ ...scrollContainerStyle, pt: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  Resultados ({vehiculos.length})
                </Typography>

                <TableContainer component={Box}>
                  <Table
                    size="small"
                    stickyHeader
                    aria-label="tabla de resultados de vehículos"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 52 }} />
                        <TableCell>Placa · Alias</TableCell>
                        <TableCell>Modem</TableCell>
                        <TableCell>Razon social</TableCell>
                        <TableCell>Último</TableCell>
                        <TableCell>Fuente</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vehiculos.map((v, idx) => {
                        const fuenteColor =
                          coloresFuente[v.fuente] || "default";
                        const iconoFuente =
                          iconosFuente[v.fuente] || null;
                        const isSelected = selectedIndex === idx;

                        return (
                          <TableRow
                            key={`${v.placa ?? "no-placa"}-${idx}`}
                            hover
                            selected={isSelected}
                            onClick={() => setSelectedIndex(idx)}
                            sx={{
                              cursor: "pointer",
                              "&.Mui-selected td": {
                                backgroundColor:
                                  theme.palette.action.selected,
                              },
                            }}
                          >
                            <TableCell sx={{ py: 0.5 }}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  fontSize: 12,
                                  bgcolor: isSelected
                                    ? "primary.dark"
                                    : "info.main",
                                  color: "white",
                                }}
                              >
                                {String(v.placa ?? "?").slice(0, 3)}
                              </Avatar>
                            </TableCell>

                            <TableCell sx={{ py: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                                noWrap
                              >
                                {v.placa ?? "—"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                {v.alias ?? "Sin alias"}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 0.5 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                              >
                                {v.modem || "—"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                {v.extras?.emai ?? ""}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 0.5, maxWidth: 180 }}>
                              {(() => {
                                const txt =
                                  v?.extras?.nombre_normal ??
                                  v?.extras?.vehAlias ??
                                  v?.extras?.CLINOMBRE ??
                                  "—";
                                return (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    title={txt}
                                    sx={{
                                      display: "block",
                                      maxWidth: 180,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {txt}
                                  </Typography>
                                );
                              })()}
                            </TableCell>

                            <TableCell sx={{ py: 0.5 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontWeight: 700 }}
                                noWrap
                              >
                                {v.fecha_ultimo_registro === ""
                                  ? v.extras?.nombre_ypfb || "—"
                                  : v.fecha_ultimo_registro}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.primary"
                                noWrap
                              >
                                {v.fecha_ultimo_registro === ""
                                  ? v.extras?.descripcion_ypfb
                                  : ""}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 0.5 }}>
                              <Chip
                                size="small"
                                label={v.fuente ?? "—"}
                                color={fuenteColor}
                                icon={iconoFuente}
                                sx={{ height: 22, fontSize: "0.75rem" }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {vehiculos.length === 0 && resultado !== null && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              No se encontraron vehículos.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>

            {/* Columna derecha: detalle del seleccionado */}
            <Box
              sx={{
                width: isMobile ? "100%" : "45%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ ...scrollContainerStyle, p: 3, pt: 2 }}>
                {!seleccionado ? (
                  <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      Selecciona un vehículo de la tabla para ver sus detalles y
                      ubicación.
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={2.5}>
                    {/* Info general */}
                    <Paper variant="outlined">
                      <Box sx={{ p: 2 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}
                          mb={1}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold" }}
                          >
                            {seleccionado.placa} · {seleccionado.alias}
                          </Typography>
                          <Chip
                            label={seleccionado.fuente}
                            color={
                              coloresFuente[seleccionado.fuente] || "default"
                            }
                            size="medium"
                            icon={
                              iconosFuente[seleccionado.fuente] || undefined
                            }
                            sx={{ fontWeight: "bold" }}
                          />
                        </Stack>

                        <Divider sx={{ my: 1 }} />

                        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Último registro:</strong>{" "}
                              {seleccionado.fecha_ultimo_registro ?? "—"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Velocidad:</strong>{" "}
                              {seleccionado?.extras?.vehVelocidadU != null
                                ? `${seleccionado.extras.vehVelocidadU} km/h`
                                : "—"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Latitud:</strong>{" "}
                              {seleccionado.latitud ?? "—"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Longitud:</strong>{" "}
                              {seleccionado.longitud ?? "—"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={12}>
                            <Typography variant="body2">
                              <strong>Punto cercano:</strong>{" "}
                              {seleccionado?.extras?.vehPuntoCercanoU ?? "—"}
                            </Typography>
                          </Grid>
                          {seleccionado.modem && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                <strong>Módem:</strong> {seleccionado.modem}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Paper>

                    {/* Mapa */}
                    <Paper variant="outlined">
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ mb: 1, fontWeight: "bold" }}
                        >
                          Ubicación Actual
                        </Typography>
                        <MiniMapaVehiculo
                          key={`${seleccionado.latitud}-${seleccionado.longitud}`}
                          latitud={seleccionado.latitud}
                          longitud={seleccionado.longitud}
                          alias={seleccionado.alias}
                          velocidad={seleccionado?.extras?.vehVelocidadU}
                          initialTipoMapa="light"
                        />
                      </Box>
                    </Paper>

                    {/* Extras */}
                    <Paper variant="outlined">
                      <Box sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ mb: 1, fontWeight: "bold" }}
                        >
                          Datos adicionales
                        </Typography>
                        <ExtrasView seleccionado={seleccionado} />
                      </Box>
                    </Paper>
                  </Stack>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehiculoViewerDialog;
